import * as Sentry from '@sentry/react';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getPhone } from '@/lib/getPhone.ts';
import { getUserReservation, getRestaurant, getReservationMetadata } from '@/lib/api.ts';
import { ApiError } from '@/lib/ApiError.ts';
import type { ReservationMetadataResponse } from '@/lib/api.ts';
import MapView from '@/components/MapView/MapView.tsx';
import type {
  EventReservationData,
  GetReservation,
  ReservationResponseReservation,
  RestaurantResponseData,
} from '@/lib/types.ts';
import { getRestaurantImage } from '@/lib/types.ts';
import { reset as resetAnalytics } from '@/lib/analytics/index.ts';
import { IS_EMBED } from '@/lib/isEmbed.ts';
import {
  MINIMUM_SPEND_OVERAGE_PAYMENT_NOTE,
  MINIMUM_SPEND_PAYMENT_CAPTION,
  showsMinimumSpendExplanation,
} from '@/lib/labels.ts';

/**
 * Navigate to the cancel flow. In the SPA this is a same-origin route. In the
 * embed the flow lives on the restaurant's origin, where `/cancel/...` does not
 * exist and a same-origin nav would destroy the overlay — so open the canonical
 * hosted cancel page on book.dorsia.com in a new tab instead.
 */
const goToCancel = (uuid: string, sig: string) => {
  const path = `/cancel/${uuid}?sig=${encodeURIComponent(sig)}`;
  if (IS_EMBED) {
    window.open(`https://book.dorsia.com${path}`, '_blank', 'noopener');
  } else {
    window.location.href = path;
  }
};
import {
  AuthAtom,
  EventReservationAtom,
  PaymentMethodsByIdAtom,
  ReservationAtom,
  RestaurantAtom,
  RestaurantConfirmationAtom,
  useRestaurantId,
  useUserDetails,
  useWidgetState,
} from '../lib/state.ts';
import type { UserDetails, WidgetState } from '../lib/types.ts';
import NeedToKnows from '@/components/NeedToKnows/NeedToKnows.tsx';
import DorsiaAppIcon from '@/assets/images/App_Icon.png';
import AppStoreBadge from '@/assets/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg?react';
import PlaidIcon from '@/assets/cards/PlaidIcon.svg?react';
import Card, { type CardNames } from '@/components/Card/Card.tsx';
import { RestaurantAmountDueRow } from '../Payment/RestaurantMinimumSpendTotals.tsx';
import styles from './ConfirmedPage.module.css';

/**
 * ConfirmedPage - Unified confirmation screen for both event and restaurant bookings.
 *
 * Since resetState() clears the widget state before navigating here, we cannot
 * rely on widget.bookingType to determine which layout to use. Instead, we check
 * which atom has data:
 * - EventReservationAtom has data -> event confirmation layout
 * - ReservationAtom has data -> restaurant confirmation layout
 * - Neither -> generic fallback with "check your email" message
 *
 * Features:
 * - Download App card (event bookings)
 * - Need to Know section
 * - Order summary with map, directions, contact links
 * - Price per person display (restaurant bookings)
 */
function ConfirmedPage() {
  const { uuid } = useParams<{ uuid?: string }>();
  const location = useLocation();
  const auth = useAtomValue(AuthAtom);
  const setAuth = useSetAtom(AuthAtom);

  // Payment info passed from useSubmitBooking via navigation state
  const navPayment = (location.state ?? {}) as {
    payment_method_id?: string;
    ach_bank_name?: string;
    ach_bank_last4?: string;
    card_brand?: string;
    card_last4?: string;
    is_bank?: boolean;
  };
  const eventReservation = useAtomValue(EventReservationAtom);
  const restaurantReservation = useAtomValue(ReservationAtom);
  const restaurantConfirmation = useAtomValue(RestaurantConfirmationAtom);
  const [storedRestaurant, setStoredRestaurant] = useAtom(RestaurantAtom);
  const restaurantId = useRestaurantId();
  const [widget, updateWidget] = useWidgetState();

  const [localRestaurant, setLocalRestaurant] = useState<RestaurantResponseData | null>(
    storedRestaurant
  );

  /** Fetch restaurant data when the atom is empty (e.g. after page refresh). */
  useEffect(() => {
    if (storedRestaurant) {
      setLocalRestaurant(storedRestaurant);
      return;
    }
    if (!restaurantId) return;
    const date = new Date().toISOString().slice(0, 10);
    void getRestaurant(restaurantId, date, '0:00', 'confirmed', auth?.token ?? null).then((res) => {
      if (res?.data) {
        setLocalRestaurant(res.data);
        setStoredRestaurant(res.data);
      }
    });
  }, [restaurantId, storedRestaurant, auth?.token]);

  const cancelEnabled = !!localRestaurant?.features?.WidgetCancelReservation;
  const user = useUserDetails();
  const navigate = useNavigate();

  // Fetch reservation by UUID if present in URL
  const [fetchedReservation, setFetchedReservation] = useState<GetReservation | null>(null);
  const [fetchLoading, setFetchLoading] = useState(!!uuid);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [needsAuth, setNeedsAuth] = useState(!!uuid && !auth?.token);

  const [metadata, setMetadata] = useState<ReservationMetadataResponse | null>(null);
  useEffect(() => {
    if (!uuid || !needsAuth) return;
    void getReservationMetadata(uuid)
      .then(setMetadata)
      .catch(() => {});
  }, [uuid, needsAuth]);

  useEffect(() => {
    if (!uuid || !auth?.token) {
      setFetchLoading(false);
      return;
    }

    const fetchReservation = async () => {
      try {
        const response = await getUserReservation(auth.token, uuid);
        if (response.reservation) {
          setFetchedReservation(response.reservation);
        } else {
          setFetchError('Reservation not found.');
        }
      } catch (e) {
        if (e instanceof ApiError && e.type === 'UnauthorizedErrorResponse') {
          setNeedsAuth(true);
        } else {
          Sentry.captureException(e);
          setFetchError('Unable to load reservation details.');
        }
      } finally {
        setFetchLoading(false);
      }
    };

    void fetchReservation();
  }, [uuid, auth?.token]);

  // Mark the embed session completed once an actual confirmation is shown, so a
  // later overlay reopen starts a fresh booking rather than resuming this one
  // (see shouldResetEmbedSession, rule (b)). Stays in the in-memory embed session
  // only — never written to partner-origin storage. Guarded so it runs once.
  const completedMarked = useRef(false);
  useEffect(() => {
    if (
      !completedMarked.current &&
      (fetchedReservation || restaurantReservation || eventReservation?.event)
    ) {
      completedMarked.current = true;
      updateWidget({ completed: true });
    }
  }, [fetchedReservation, restaurantReservation, eventReservation]);

  const onRestart = () => {
    // Starting a new reservation from the confirmation page: clear the completed
    // marker so a mid-flow close/reopen of this NEW booking resumes rather than
    // being wiped as a finished session (shouldResetEmbedSession rule (b)).
    updateWidget({ completed: false });
    navigate('../booking');
  };

  // Loading state for UUID-based fetch
  if (fetchLoading) {
    return (
      <div className={styles.eventContent}>
        <div className={styles.card} style={{ padding: 40, textAlign: 'center' }}>
          <p style={{ color: '#8d8d8d' }}>Loading reservation...</p>
        </div>
      </div>
    );
  }

  // UUID fetch succeeded — render as restaurant confirmation
  // (GetReservation shape is compatible with ReservationResponseReservation)
  if (fetchedReservation) {
    return (
      <RestaurantConfirmationLayout
        reservation={fetchedReservation as unknown as ReservationResponseReservation}
        onRestart={onRestart}
        navPayment={navPayment}
        reservationUuid={uuid}
        authToken={auth?.token}
        cancellationPolicy={fetchedReservation.cancellation_policy}
        cancelEnabled={cancelEnabled}
        showMinimumSpendExplanation={showsMinimumSpendExplanation(localRestaurant?.features)}
      />
    );
  }

  // UUID fetch failed
  if (fetchError && uuid) {
    return (
      <div className={styles.eventContent}>
        <div className={styles.card} style={{ padding: 40, textAlign: 'center' }}>
          <h2 className={styles.sectionHeadingMedium}>{fetchError}</h2>
          <button onClick={onRestart} className={styles.secondaryButton} style={{ marginTop: 16 }}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  // Determine which booking type we have based on available atom data
  const isEventBooking = !!eventReservation?.event;
  const isRestaurantBooking = !isEventBooking && !!restaurantReservation;

  // Dev bypass: show a mock confirmation to preview the map/layout
  // Navigate to /restaurant/794/confirmed?dev=1
  const devParam = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get('dev')
    : null;
  const devBypass = !!devParam;

  if (devBypass && !isEventBooking && !isRestaurantBooking) {
    // ?dev=1 — basic confirmed layout
    // ?dev=ach — ACH pending layout with Payment Processing + Payment Method
    const devMode = devParam;

    const mockReservation = {
      party_size: 2,
      table_type: '50 Minute Omakase Tasting Menu',
      table_minimum: 750,
      table_minimum__currency: '$750',
      price_per_person: 375,
      price_per_person__currency: '$375',
      reservation_at: '2026-04-10T18:00:00-07:00',
      confirmed_at: devMode === 'ach' ? null : '2026-04-10T18:01:00-07:00',
      failed_at: null,
      card: devMode === 'ach' ? null : { brand: 'Visa', number: '4242' },
      credits_used: 0,
      credits_used__currency: '',
      user: {
        first_name: 'Marc',
        last_name: 'Lotenberg',
        phone: '+11123456',
        email: 'marc@dorsia.com',
      },
      restaurant: {
        name: 'Nobu at The Red Bull Mirage',
        address: '53153 Jackson St\nCoachella, CA 92236',
        neighborhood_name: 'Coachella',
        phone: '+17023888338',
        timezone: 'America/Los_Angeles',
        latitude: 33.6803,
        longitude: -116.2368,
        squarish_crop_image_url:
          'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200&h=200&fit=crop',
        need_to_knows: [
          { icon: 'info', description: 'Located on first floor of Red Bull Mirage facing quasar.' },
          {
            icon: 'info',
            description:
              'All guests attending the reservation need to have valid festival wristbands.',
          },
          {
            icon: 'prohibited',
            description:
              'This experience has limited capacity, so this booking is 100% prepaid and nonrefundable.',
          },
          {
            icon: 'info',
            description:
              'Booking a reservation provides access to level one for the entirety of the booking day.',
          },
        ],
        website: 'Zenyara.com',
        allow_alternative_pickup: devMode === 'ach',
      },
    } as unknown as ReservationResponseReservation;

    const mockNavPayment: NavPayment =
      devMode === 'ach'
        ? {
            is_bank: true,
            ach_bank_name: 'Chase',
            ach_bank_last4: '1897',
            card_brand: 'Visa',
            card_last4: '4242',
          }
        : {};

    return (
      <RestaurantConfirmationLayout
        reservation={mockReservation}
        onRestart={onRestart}
        navPayment={mockNavPayment}
      />
    );
  }

  // UUID present but user needs to authenticate to view reservation
  if (needsAuth) {
    return (
      <div className={styles.eventContent}>
        <div className="flex flex-col items-center gap-4 rounded-[20px] bg-white p-10 text-center">
          <h2 className={styles.sectionHeadingMedium}>
            Verify your identity to view reservation details.
          </h2>
          {metadata && metadata.restaurant_name && (
            <div className="space-y-1 text-sm text-gray-500">
              <p className="font-medium text-gray-700">{metadata.restaurant_name}</p>
              {metadata.date && (
                <p>
                  {metadata.date}
                  {metadata.time ? ` at ${metadata.time}` : ''}
                </p>
              )}
              {metadata.party_size && (
                <p>
                  {metadata.party_size} guest{metadata.party_size !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}
          <p className="mb-5 mt-2 text-sm text-gray-500">
            Please verify your phone number to access your reservation.
          </p>
          <button
            onClick={() => {
              setAuth(null);
              // Drop the stale Segment identity too, so the re-verification
              // funnel that follows isn't attributed to the previous member.
              resetAnalytics();
              navigate(`../details?returnTo=confirmed/${uuid}`);
            }}
            className={styles.primaryButton}
          >
            Verify Phone Number
          </button>
        </div>
      </div>
    );
  }

  // Fallback: no reservation data at all (came from a direct navigation without booking)
  if (!isEventBooking && !isRestaurantBooking) {
    return (
      <div className={styles.eventContent}>
        <div className={styles.card} style={{ padding: 40, textAlign: 'center' }}>
          <h2 className={styles.sectionHeadingMedium}>
            Please check your email for reservation details.
          </h2>
          <button onClick={onRestart} className={styles.secondaryButton} style={{ marginTop: 16 }}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  // --- Event Booking Confirmation ---
  if (isEventBooking && eventReservation) {
    return (
      <EventConfirmationLayout
        reservation={eventReservation}
        restaurant={localRestaurant}
        widget={widget}
        user={user}
      />
    );
  }

  // --- Restaurant Booking Confirmation ---
  if (isRestaurantBooking && restaurantReservation) {
    return (
      <RestaurantConfirmationLayout
        reservation={restaurantReservation}
        onRestart={onRestart}
        navPayment={navPayment}
        reservationUuid={restaurantReservation.uuid}
        authToken={auth?.token}
        cancellationPolicy={
          restaurantReservation.cancellation_policy ?? restaurantConfirmation?.cancellation_policy
        }
        cancelEnabled={cancelEnabled}
        showMinimumSpendExplanation={showsMinimumSpendExplanation(localRestaurant?.features)}
      />
    );
  }

  return null;
}

// ============================================================================
// Event Confirmation Layout
// ============================================================================

interface EventConfirmationLayoutProps {
  reservation: EventReservationData;
  restaurant: RestaurantResponseData | null;
  widget: WidgetState;
  user: UserDetails;
}

function EventConfirmationLayout({
  reservation,
  restaurant,
  widget,
  user,
}: EventConfirmationLayoutProps) {
  const methodsById = useAtomValue(PaymentMethodsByIdAtom);
  const paymentMethod = widget.payment_method ? methodsById[widget.payment_method] : null;
  const restData = reservation.restaurant ?? restaurant;
  const restPhone = getPhone(restData?.phone);
  const flat_address = restData?.address ? restData.address.replace(/[\r\n]+/g, ', ') : '';

  // User contact info
  const mainContact = user ?? reservation.user;
  const userPhone = getPhone(mainContact?.phone);
  const altPickup = restaurant?.allow_alternative_pickup
    ? (reservation.alternative_pickup_contact ??
      (widget.alternative_pickup_first_name
        ? {
            name: `${widget.alternative_pickup_first_name} ${widget.alternative_pickup_last_name ?? ''}`.trim(),
            phone: widget.alternative_pickup_phone ?? '',
            email: widget.alternative_pickup_email ?? '',
          }
        : null))
    : null;

  // Pricing
  const eventInfo = widget.eventInfo;
  const taxAmount = eventInfo?.tax_amount ?? 0;
  const taxRate = eventInfo?.tax_rate ?? 0;
  const totalPaid = eventInfo?.total_paid ?? reservation.total_paid ?? 0;
  const subtotal = totalPaid - taxAmount;

  // A non-ticketed event is a plain reservation, so it reuses the restaurant
  // confirmation copy instead of promising a ticket (EVNT-1818).
  const isNonTicketed = reservation.event?.non_ticketed === true;

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  return (
    <>
      {/* Main heading */}
      <h1 className={styles.confirmationHero}>
        {isNonTicketed ? 'Booking confirmed' : 'All set! Just download the app for your ticket in.'}
      </h1>

      {/* Two-column layout */}
      <div className={`${styles.twoColumnLayout} ${styles.confirmationSummaryLayout}`}>
        {/* Left column: Download App + Need to Know */}
        <div className={styles.mainColumn}>
          {/* Download Dorsia App card */}
          <a
            className={styles.downloadSection}
            href="https://apps.apple.com/us/app/dorsia-members-club/id1589267208"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={DorsiaAppIcon} alt="Dorsia App" className={styles.downloadIcon} />
            <h3 className={styles.downloadTitle}>Download the Dorsia App</h3>
            <p className={styles.downloadText}>
              {isNonTicketed
                ? 'Download the Dorsia app to manage your reservations, view your dining history, and discover new restaurants.'
                : 'Download the Dorsia app. Dorsia is your ticket in. Every guest will need the app to enter, so make sure to set up your account before the event.'}
            </p>
            <AppStoreBadge className={styles.appStoreButton} />
          </a>

          {/* Need to know */}
          {restData?.need_to_knows && restData.need_to_knows.length > 0 && (
            <div className={styles.card} style={{ marginTop: 20 }}>
              <div className={styles.needToKnow}>
                <p className={styles.sectionHeadingMedium}>Need to know</p>
                <Suspense fallback={<div>Loading...</div>}>
                  <NeedToKnows need_to_knows={restData.need_to_knows} />
                </Suspense>
              </div>
            </div>
          )}
        </div>

        {/* Right column: Order summary */}
        <div className={styles.sidebarColumn}>
          <div className={styles.orderSummary}>
            <p className={styles.orderSummaryTitle}>{restData?.name}</p>
            {restData?.address && <p className={styles.orderSummaryAddress}>{restData.address}</p>}

            {/* Contact info */}
            {mainContact && (
              <>
                <div className={styles.orderSummaryDivider} />
                <div className={styles.contactColumns}>
                  <div className={styles.contactColumn}>
                    <p className={styles.contactLabel}>MEMBER DETAILS</p>
                    <p className={styles.contactValue}>
                      {mainContact.first_name} {mainContact.last_name}
                    </p>
                    <p className={styles.contactValue}>
                      {userPhone ? userPhone.formatNational() : mainContact.phone}
                    </p>
                    <p className={styles.contactValue}>{mainContact.email}</p>
                  </div>
                  {altPickup && (
                    <div className={styles.contactColumn}>
                      <p className={styles.contactLabel}>ALTERNATE PICKUP</p>
                      <p className={styles.contactValue}>{altPickup.name}</p>
                      <p className={styles.contactValue}>{altPickup.phone}</p>
                      <p className={styles.contactValue}>{altPickup.email}</p>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className={styles.orderSummaryDivider} />

            {/* Checkout items — grouped by name */}
            {(() => {
              const items = reservation.items ?? eventInfo?.items ?? [];
              const grouped = new Map<
                string,
                { name: string; image?: string; quantity: number; price__currency?: string }
              >();
              for (const item of items) {
                const key = item.item?.name ?? '';
                const existing = grouped.get(key);
                if (existing) {
                  existing.quantity += item.quantity ?? 1;
                } else {
                  grouped.set(key, {
                    name: key,
                    image: item.item?.image,
                    quantity: item.quantity ?? 1,
                    price__currency: item.price__currency,
                  });
                }
              }
              const groupedItems = Array.from(grouped.values());

              if (groupedItems.length > 0) {
                return groupedItems.map((item, idx) => (
                  <div key={idx} className={styles.orderSummaryBooking}>
                    {(item.image || reservation.event?.card_image?.original_url) && (
                      <img
                        src={item.image || reservation.event?.card_image?.original_url}
                        alt={item.name || 'Event'}
                        className={styles.orderSummaryImage}
                      />
                    )}
                    <div className={styles.orderSummaryBookingDetails}>
                      <p className={styles.orderSummaryBookingName}>
                        {item.name}
                        {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                      </p>
                      {reservation.event?.datetime_local__formatted && (
                        <>
                          <p className={styles.orderSummaryBookingInfo}>
                            {reservation.event.datetime_local__formatted.date_dow_short},{' '}
                            {reservation.event.datetime_local__formatted.date_long}
                          </p>
                          <p className={styles.orderSummaryBookingInfo}>
                            {reservation.event.datetime_local__formatted.time}
                          </p>
                        </>
                      )}
                      {item.price__currency && (
                        <p className={styles.orderSummaryBookingInfo}>{item.price__currency}</p>
                      )}
                    </div>
                  </div>
                ));
              }

              return (
                <div className={styles.orderSummaryBooking}>
                  {reservation.event?.card_image?.original_url && (
                    <img
                      src={reservation.event.card_image.original_url}
                      alt={reservation.event.name ?? 'Event'}
                      className={styles.orderSummaryImage}
                    />
                  )}
                  <div className={styles.orderSummaryBookingDetails}>
                    <p className={styles.orderSummaryBookingName}>
                      {reservation.event?.name ?? restData?.name}
                    </p>
                    {reservation.event?.datetime_local__formatted && (
                      <>
                        <p className={styles.orderSummaryBookingInfo}>
                          {reservation.event.datetime_local__formatted.date_dow_short},{' '}
                          {reservation.event.datetime_local__formatted.date_long}
                        </p>
                        <p className={styles.orderSummaryBookingInfo}>
                          {reservation.event.datetime_local__formatted.time}
                        </p>
                      </>
                    )}
                    <p className={styles.orderSummaryBookingInfo}>
                      {reservation.party_size} guest
                      {reservation.party_size !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              );
            })()}

            <div className={styles.orderSummaryDivider} />

            {/* Pricing */}
            <div className={styles.orderSummaryPriceRow}>
              <p className={styles.orderSummaryPriceLabel}>Subtotal</p>
              <p className={styles.orderSummaryPriceValue}>{formatCurrency(subtotal)}</p>
            </div>
            {taxAmount > 0 && (
              <div className={styles.orderSummaryPriceRow}>
                <p className={styles.orderSummaryPriceLabel}>Tax ({(taxRate * 100).toFixed(2)}%)</p>
                <p className={styles.orderSummaryPriceValue}>{formatCurrency(taxAmount)}</p>
              </div>
            )}

            {/* Service charges */}
            {reservation.service_charges__currency?.map(
              (charge: { label: string; amount: string }, idx: number) => (
                <div className={styles.orderSummaryPriceRow} key={idx}>
                  <p className={styles.orderSummaryPriceLabel}>{charge.label}</p>
                  <p className={styles.orderSummaryPriceValue}>{charge.amount}</p>
                </div>
              )
            )}

            {/* Credits */}
            {(eventInfo?.credits_used__currency || reservation.credits_used__currency) && (
              <div className={styles.orderSummaryPriceRow}>
                <p className={styles.orderSummaryPriceLabel}>Credits</p>
                <p className={styles.orderSummaryPriceValue}>
                  -{eventInfo?.credits_used__currency ?? reservation.credits_used__currency}
                </p>
              </div>
            )}

            {/* Fun Coupons */}
            {(() => {
              const funCoupons = eventInfo?.points_used_value ?? reservation.points_used_value ?? 0;
              return funCoupons > 0 ? (
                <div className={styles.orderSummaryPriceRow}>
                  <p className={styles.orderSummaryPriceLabel}>Fun Coupons</p>
                  <p className={styles.orderSummaryPriceValue}>-{formatCurrency(funCoupons)}</p>
                </div>
              ) : null;
            })()}

            <div className={styles.orderSummaryDivider} />

            <div className={styles.orderSummaryTotal}>
              <p className={styles.orderSummaryTotalLabel}>Total</p>
              <p className={styles.orderSummaryTotalValue}>
                {reservation.total_paid__currency ?? formatCurrency(totalPaid)}
              </p>
            </div>

            {/* Amount charged + Payment method */}
            {paymentMethod && (
              <>
                <div className={styles.orderSummaryDivider} />
                <div className={styles.orderSummaryPriceRow}>
                  <p className={styles.orderSummaryPriceLabel}>Amount charged</p>
                  <p className={styles.orderSummaryPriceValue}>
                    {reservation.total_paid__currency ?? formatCurrency(totalPaid)}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-8 flex-shrink-0">
                    <Card
                      name={(paymentMethod.brand?.toLowerCase() ?? 'visa') as CardNames}
                      className=""
                    />
                  </div>
                  <p className={styles.orderSummaryPriceLabel}>ending in {paymentMethod.number}</p>
                </div>
              </>
            )}

            {/* Map */}
            {restData?.address && <MapView restaurant={restData} height={200} />}

            {/* Directions button */}
            {restData?.address && (
              <a
                className={styles.directionsButton}
                href={`https://maps.google.com/?q=${encodeURIComponent(flat_address)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Directions
              </a>
            )}

            {/* Phone + Website links */}
            <div className={styles.contactLinks}>
              {restPhone && (
                <a href={restPhone.getURI()} className={styles.contactLink}>
                  {restPhone.formatNational()}
                </a>
              )}
              {restData?.website && (
                <a
                  href={restData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.contactLink}
                >
                  {restData.website.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Restaurant Confirmation Layout
// ============================================================================

interface NavPayment {
  payment_method_id?: string;
  ach_bank_name?: string;
  ach_bank_last4?: string;
  card_brand?: string;
  card_last4?: string;
  is_bank?: boolean;
}

interface RestaurantConfirmationLayoutProps {
  reservation: ReservationResponseReservation;
  onRestart: () => void;
  navPayment?: NavPayment;
  reservationUuid?: string;
  authToken?: string;
  cancellationPolicy?: string | null;
  cancelEnabled?: boolean;
  /** True shows the food-and-drink caption. The parent reads the hide flag. */
  showMinimumSpendExplanation?: boolean;
}

function RestaurantConfirmationLayout({
  reservation,
  onRestart,
  navPayment,
  reservationUuid,
  authToken,
  cancellationPolicy,
  cancelEnabled = false,
  showMinimumSpendExplanation = true,
}: RestaurantConfirmationLayoutProps) {
  // Payment is pending when:
  // - confirmed_at is null (ACH takes 96h to settle)
  // - ach_payment_status is 'processing' (explicit ACH status from backend)
  // - navPayment.is_bank is set (secondary signal for initial navigation)
  const isPaymentPending =
    reservation.confirmed_at === null ||
    reservation.ach_payment_status === 'processing' ||
    (!!navPayment?.is_bank && !reservation.confirmed_at);

  // Bank display from nav state (Plaid ACH)
  const bankName = navPayment?.ach_bank_name ?? 'Bank';
  const bankLast4 = navPayment?.ach_bank_last4;
  const hasBankInfo = isPaymentPending && !!bankLast4;

  // Backup card display (in dual-payment ACH flow, the card is for incidentals)
  const card = reservation.card as unknown as { brand?: string; number?: string } | string | null;
  const backupCardBrand =
    (card && typeof card === 'object' ? card.brand : null) ?? navPayment?.card_brand;
  const backupCardLast4 =
    (card && typeof card === 'object' ? card.number : null) ?? navPayment?.card_last4;
  const hasBackupCard = isPaymentPending && !!backupCardBrand;

  const funCouponsCurrency =
    (reservation as unknown as { points_used_currency_value__currency?: string | null })
      .points_used_currency_value__currency ?? null;

  const isMinSpendBooking = reservation.table_minimum > 0;

  const feeRestaurantCurrency =
    (reservation as unknown as { fee_restaurant__currency?: string }).fee_restaurant__currency ||
    reservation.total_prepayment_amount__currency ||
    null;
  const taxRate = reservation.tax_rate;
  const taxCurrency = reservation.tax_amount__currency;
  const serviceCharges = reservation.service_charges__currency ?? [];

  const hasDeductions = reservation.credits_used > 0 || !!funCouponsCurrency;

  const restData = reservation.restaurant;
  const flat_address = restData?.address ? restData.address.replace(/[\r\n]+/g, ', ') : '';
  const user_phone = getPhone(reservation.user.phone);
  const rest_phone = getPhone(restData?.phone);
  const tzd = dayjs(reservation.reservation_at).tz(restData.timezone);

  const isCancelled = !!(reservation as unknown as { cancelled?: boolean }).cancelled;
  const showCancelButton = cancelEnabled && !isCancelled && !!reservationUuid;

  const navigate = useNavigate();
  /**
   * Handles cancel button click.
   * Navigates to the existing cancel page. The cancel route loader accepts
   * either a signature in the URL or an authenticated widget session.
   */
  const [cancelLoading, setCancelLoading] = useState(false);

  const handleCancelClick = async () => {
    if (!reservationUuid) return;

    const sig = (reservation as unknown as { hash_signature?: string }).hash_signature;

    if (sig) {
      goToCancel(reservationUuid, sig);
      return;
    }

    // No signature on the reservation — fetch it from the API
    if (authToken) {
      setCancelLoading(true);
      try {
        const res = await getUserReservation(authToken, reservationUuid);
        const fetchedSig = res?.reservation?.hash_signature;
        if (fetchedSig) {
          goToCancel(reservationUuid, fetchedSig);
          return;
        }
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        setCancelLoading(false);
      }
    }
  };

  return (
    <>
      {/* Main heading — ACH payments show processing message */}
      <h1 className={styles.confirmationHero}>
        {isPaymentPending
          ? 'All set! Download the app to see the status of your booking.'
          : isCancelled
            ? 'Booking cancelled'
            : 'Booking confirmed'}
      </h1>

      {/* Two-column layout */}
      <div className={`${styles.twoColumnLayout} ${styles.confirmationSummaryLayout}`}>
        {/* Left column: Reservation details + Need to Know + Download */}
        <div className={styles.mainColumn}>
          {/* Payment Processing banner for ACH — Figma: bg #FFFBF2, p-20, gap-20 */}
          {isPaymentPending && (
            <div
              style={{
                background: '#FFFBF2',
                borderRadius: 0,
                display: 'flex',
                gap: 20,
                alignItems: 'flex-start',
                padding: 20,
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                style={{ flexShrink: 0 }}
              >
                <circle cx="12" cy="12" r="8" stroke="#0d0d0d" strokeWidth="1.2" />
                <path
                  d="M12 8v4l2.5 2.5"
                  stroke="#0d0d0d"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, flex: 1 }}>
                <p
                  style={{
                    fontFamily: "'Sailec', sans-serif",
                    fontWeight: 500,
                    fontSize: 20,
                    lineHeight: 1.25,
                    color: '#000',
                    margin: 0,
                  }}
                >
                  Payment Processing
                </p>
                <p
                  style={{
                    fontFamily: "'Sailec', sans-serif",
                    fontWeight: 400,
                    fontSize: 15,
                    lineHeight: 1.5,
                    color: '#000',
                    margin: 0,
                  }}
                >
                  Your booking is being held for the next 96 hours while your payment processes. We
                  will notify you via SMS and the app when your payment is confirmed.
                </p>
              </div>
            </div>
          )}

          {/* Need to know */}
          {restData?.need_to_knows && restData.need_to_knows.length > 0 && (
            <div className={styles.card} style={isPaymentPending ? { marginTop: 20 } : undefined}>
              <div className={styles.needToKnow}>
                <p className={styles.sectionHeadingMedium}>Need to know</p>
                <Suspense fallback={<div>Loading...</div>}>
                  <NeedToKnows need_to_knows={restData.need_to_knows} />
                </Suspense>
              </div>
            </div>
          )}

          {/* Cancellation policy */}
          {!isCancelled && cancellationPolicy && (
            <div className={styles.card} style={{ marginTop: 20 }}>
              <div className={styles.needToKnow}>
                <p className={styles.sectionHeadingMedium}>Cancellation policy</p>
                <p className={styles.cancellationPolicyText}>{cancellationPolicy}</p>
              </div>
            </div>
          )}

          {/* Download App card */}
          <a
            className={`${styles.downloadSection} mt-5`}
            href="https://apps.apple.com/us/app/dorsia-members-club/id1589267208"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={DorsiaAppIcon} alt="Dorsia App" className={styles.downloadIcon} />
            <h3 className={styles.downloadTitle}>Download the Dorsia App</h3>
            <p className={styles.downloadText}>
              Download the Dorsia app to manage your reservations, view your dining history, and
              discover new restaurants.
            </p>
            <AppStoreBadge className={styles.appStoreButton} />
          </a>

          {/* New reservation button */}
          <button className={styles.secondaryButton} onClick={onRestart} style={{ marginTop: 16 }}>
            Create a New Reservation
          </button>

          {showCancelButton && (
            <button
              onClick={() => void handleCancelClick()}
              className={styles.secondaryButton}
              style={{ marginTop: 8 }}
              disabled={cancelLoading}
            >
              <span className="text-red-500">
                {cancelLoading ? 'Loading...' : 'Cancel Reservation'}
              </span>
            </button>
          )}

          <div className="pb-8" />
        </div>

        {/* Right column: Order summary with map */}
        <div className={styles.sidebarColumn}>
          <div className={styles.orderSummary}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <p className={styles.orderSummaryTitle}>{restData.name}</p>
              {isPaymentPending && (
                <span
                  style={{
                    fontFamily: "'Sailec', sans-serif",
                    fontSize: 10,
                    fontWeight: 500,
                    lineHeight: 1.25,
                    background: '#E5CD59',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: 20,
                    flexShrink: 0,
                    marginLeft: 8,
                  }}
                >
                  Processing
                </span>
              )}
            </div>
            {restData.address && (
              <div
                className={styles.orderSummaryAddress}
                style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
              >
                {restData.address}
              </div>
            )}

            <div className={styles.orderSummaryDivider} />

            {/* Contact info (Figma node 1401:854) */}
            {reservation.user && (
              <>
                <div style={{ display: 'flex', gap: 40, width: '100%', lineHeight: 1.5 }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <p
                      style={{
                        fontFamily: "'Sailec', sans-serif",
                        fontWeight: 500,
                        fontSize: 12,
                        color: '#8d8d8d',
                        letterSpacing: 1.2,
                        margin: 0,
                      }}
                    >
                      MEMBER DETAILS
                    </p>
                    <div style={{ fontSize: 15, fontFamily: "'Sailec', sans-serif" }}>
                      <p style={{ margin: 0 }}>
                        {reservation.user.first_name} {reservation.user.last_name}
                      </p>
                      <p style={{ margin: 0 }}>{!!user_phone && user_phone.formatNational()}</p>
                      <p style={{ margin: 0 }}>{reservation.user.email}</p>
                    </div>
                  </div>
                  {reservation.alternative_pickup_contact && (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <p
                        style={{
                          fontFamily: "'Sailec', sans-serif",
                          fontWeight: 500,
                          fontSize: 12,
                          color: '#8d8d8d',
                          letterSpacing: 1.2,
                          margin: 0,
                        }}
                      >
                        ALTERNATE PICKUP
                      </p>
                      <div style={{ fontSize: 15, fontFamily: "'Sailec', sans-serif" }}>
                        <p style={{ margin: 0 }}>{reservation.alternative_pickup_contact.name}</p>
                        <p style={{ margin: 0 }}>{reservation.alternative_pickup_contact.phone}</p>
                        <p style={{ margin: 0 }}>{reservation.alternative_pickup_contact.email}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.orderSummaryDivider} />
              </>
            )}

            {/* Booking details */}
            <div className={styles.orderSummaryBooking}>
              {getRestaurantImage(restData) && (
                <img
                  src={getRestaurantImage(restData)!}
                  alt={restData.name}
                  className={styles.orderSummaryImage}
                />
              )}
              <div className={styles.orderSummaryBookingDetails}>
                <p className={styles.orderSummaryBookingName}>
                  {reservation.table_type ?? restData.name}
                </p>
                <p className={styles.orderSummaryBookingInfo}>{tzd.format('ddd, MMMM D')}</p>
                <p className={styles.orderSummaryBookingInfo}>{tzd.format('h:mm a')}</p>
                <p className={styles.orderSummaryBookingInfo}>
                  {reservation.party_size} guest
                  {reservation.party_size !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className={styles.orderSummaryDivider} />

            {/* Pricing — restaurant min-spend bookings */}
            {isMinSpendBooking && (
              <>
                {hasDeductions && (
                  <div className={styles.orderSummaryWalletLines}>
                    {reservation.credits_used > 0 && reservation.credits_used__currency && (
                      <div className={styles.orderSummaryPriceRow}>
                        <p className={styles.orderSummaryPriceLabel}>Credits</p>
                        <p className={styles.orderSummaryPriceValue}>
                          -{reservation.credits_used__currency}
                        </p>
                      </div>
                    )}
                    {funCouponsCurrency && (
                      <div className={styles.orderSummaryPriceRow}>
                        <p className={styles.orderSummaryPriceLabel}>Fun Coupons</p>
                        <p className={styles.orderSummaryPriceValue}>-{funCouponsCurrency}</p>
                      </div>
                    )}
                  </div>
                )}
                {reservation.total_paid > 0 && reservation.total_paid__currency && (
                  <RestaurantAmountDueRow
                    amountDueCurrency={reservation.total_paid__currency}
                    cardName={backupCardBrand ? (backupCardBrand.toLowerCase() as CardNames) : null}
                    cardLast4={backupCardLast4}
                    showLabel={false}
                  />
                )}
                <div>
                  {reservation.price_per_person > 0 && (
                    <p
                      className="text-sm text-gray-400"
                      style={{ fontFamily: "'Sailec', sans-serif", margin: '0 0 4px' }}
                    >
                      {reservation.price_per_person__currency} minimum per person
                    </p>
                  )}
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>Minimum spend</p>
                    <p className={styles.orderSummaryPriceValue}>
                      {reservation.table_minimum__currency}
                    </p>
                  </div>
                  {showMinimumSpendExplanation && (
                    <p className={styles.orderSummaryMinimumSpendNote}>
                      {MINIMUM_SPEND_PAYMENT_CAPTION}
                    </p>
                  )}
                </div>

                <div className={styles.orderSummaryDivider} />
              </>
            )}

            {/* Pricing — event / non-min-spend bookings (matches payment page) */}
            {!isMinSpendBooking && (
              <>
                {/* Subtotal */}
                {feeRestaurantCurrency && (
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>Subtotal</p>
                    <p className={styles.orderSummaryPriceValue}>{feeRestaurantCurrency}</p>
                  </div>
                )}

                {/* Tax */}
                {(reservation.tax_amount ?? 0) > 0 && taxCurrency && (
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>
                      Tax{taxRate ? ` (${taxRate}%)` : ''}
                    </p>
                    <p className={styles.orderSummaryPriceValue}>{taxCurrency}</p>
                  </div>
                )}

                {/* Service charges */}
                {serviceCharges.map((charge: { label: string; amount: string }, idx: number) => (
                  <div className={styles.orderSummaryPriceRow} key={idx}>
                    <p className={styles.orderSummaryPriceLabel}>{charge.label}</p>
                    <p className={styles.orderSummaryPriceValue}>{charge.amount}</p>
                  </div>
                ))}

                {/* Credits */}
                {reservation.credits_used > 0 && reservation.credits_used__currency && (
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>Credits</p>
                    <p className={styles.orderSummaryPriceValue}>
                      -{reservation.credits_used__currency}
                    </p>
                  </div>
                )}

                {/* Fun Coupons */}
                {funCouponsCurrency && (
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>Fun Coupons</p>
                    <p className={styles.orderSummaryPriceValue}>-{funCouponsCurrency}</p>
                  </div>
                )}

                <div className={styles.orderSummaryDivider} />

                {/* Total */}
                <div className={styles.orderSummaryTotal}>
                  <p className={styles.orderSummaryTotalLabel}>Total</p>
                  <p className={styles.orderSummaryTotalValue}>
                    {reservation.total_paid__currency}
                  </p>
                </div>
              </>
            )}

            {/* Payment Method section — matches Figma node 1401:883 */}
            {(hasBankInfo || (!isPaymentPending && backupCardBrand)) && (
              <>
                <div className={styles.orderSummaryPaymentMethod}>
                  <p className={styles.orderSummaryPaymentMethodTitle}>Payment Method</p>

                  {/* ACH bank payment row */}
                  {hasBankInfo && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 20,
                        alignItems: 'flex-start',
                        padding: '20px 0',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{ display: 'flex', flex: 1, gap: 10, alignItems: 'center' }}>
                        <PlaidIcon style={{ width: 20, height: 20, flexShrink: 0 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                          <p
                            style={{
                              fontFamily: "'Sailec', sans-serif",
                              fontWeight: 500,
                              fontSize: 15,
                              lineHeight: 1.25,
                              color: '#000',
                              margin: 0,
                            }}
                          >
                            {bankName} ···· {bankLast4}
                          </p>
                          <p
                            style={{
                              fontFamily: "'Sailec', sans-serif",
                              fontWeight: 400,
                              fontSize: 12,
                              lineHeight: 1.25,
                              color: '#8d8d8d',
                              margin: 0,
                            }}
                          >
                            ACH transfer in progress
                          </p>
                        </div>
                      </div>
                      {reservation.table_minimum > 0 && (
                        <p
                          style={{
                            fontFamily: "'Sailec', sans-serif",
                            fontWeight: 500,
                            fontSize: 15,
                            lineHeight: 1.25,
                            letterSpacing: -0.15,
                            color: '#0d0d0d',
                            margin: 0,
                            flexShrink: 0,
                            textAlign: 'right',
                          }}
                        >
                          {reservation.table_minimum__currency}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Backup card row (dual-payment ACH flow) */}
                  {hasBackupCard && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 20,
                        alignItems: 'flex-start',
                        padding: '20px 0',
                        overflow: 'hidden',
                        height: 78,
                        boxSizing: 'border-box',
                      }}
                    >
                      <div style={{ display: 'flex', flex: 1, gap: 10, alignItems: 'center' }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Card
                            name={(backupCardBrand?.toLowerCase() ?? 'visa') as CardNames}
                            className=""
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <p
                              style={{
                                fontFamily: "'Sailec', sans-serif",
                                fontWeight: 500,
                                fontSize: 15,
                                lineHeight: 1.25,
                                color: '#000',
                                margin: 0,
                              }}
                            >
                              ···· {backupCardLast4}
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: "'Sailec', sans-serif",
                              fontWeight: 400,
                              fontSize: 12,
                              lineHeight: 1.25,
                              color: '#8d8d8d',
                              margin: 0,
                            }}
                          >
                            Incidentals and overage
                          </p>
                        </div>
                      </div>
                      <p
                        style={{
                          fontFamily: "'Sailec', sans-serif",
                          fontWeight: 500,
                          fontSize: 15,
                          lineHeight: 1.25,
                          letterSpacing: -0.15,
                          color: '#0d0d0d',
                          margin: 0,
                          flexShrink: 0,
                          textAlign: 'right',
                        }}
                      >
                        Backup
                      </p>
                    </div>
                  )}

                  {!hasBankInfo && !isPaymentPending && backupCardBrand && (
                    <p className={styles.orderSummaryPaymentMethodLine}>
                      <span className={styles.orderSummaryCardIcon}>
                        <Card name={backupCardBrand.toLowerCase() as CardNames} />
                      </span>
                      <span>{backupCardLast4 ?? '····'}</span>
                    </p>
                  )}
                  {((!hasBankInfo && !!backupCardBrand) || hasBackupCard) && (
                    <p className={styles.orderSummaryOverageNote}>
                      {MINIMUM_SPEND_OVERAGE_PAYMENT_NOTE}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Map */}
            {restData.address && <MapView restaurant={restData} height={200} />}

            {/* Directions button */}
            {restData.address && (
              <a
                className={styles.directionsButton}
                href={`https://maps.google.com/?q=${encodeURIComponent(flat_address)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Directions
              </a>
            )}

            {/* Phone + Website links */}
            <div className={styles.contactLinks}>
              {rest_phone && (
                <a href={rest_phone.getURI()} className={styles.contactLink}>
                  {rest_phone.formatNational()}
                </a>
              )}
              {restData.website && (
                <a
                  href={restData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.contactLink}
                >
                  {restData.website.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ConfirmedPage;
